import React, { useRef } from 'react';
import QRCode from 'qrcode.react';
import copy from 'copy-to-clipboard';
import { Copy, Download, ExternalLink, Share2 } from 'lucide-react';
import { toast } from '@dashboard/context/ToastContext';
import downloadUrlAsFile from '@dashboard/services/downloadUrlAsFile';
import { Button } from '@dashboard/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@dashboard/components/ui/dialog';

const PNG_SIZE = 1024;

type Props = {
  /** The link people share: the store homepage when the link in bio is it, else /@store. */
  url: string;
  /** Where the Open button goes: the same link, marked as the seller's own visit. */
  openUrl: string;
  /** Always /@store, so printed codes keep working if the homepage changes. */
  qrUrl: string;
  fileName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

// Blob URLs, not data URLs: Safari opens a data URL in a tab instead of saving it.
function downloadBlob(blob: Blob, fileName: string) {
  const objectUrl = URL.createObjectURL(blob);
  downloadUrlAsFile(objectUrl, fileName);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

export default function LinkInBioShareDialog({
  url,
  openUrl,
  qrUrl,
  fileName,
  open,
  onOpenChange,
}: Props) {
  const qrShownUrl = qrUrl.split('?')[0];
  const svgRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  // qrcode.react multiplies the canvas by the device pixel ratio.
  const canvasSize = Math.round(PNG_SIZE / (window.devicePixelRatio || 1));
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const onCopy = () => {
    copy(url);
    toast({ title: 'Link copied', type: 'success' });
  };

  const onShare = () => {
    navigator.share({ url }).catch(() => undefined);
  };

  const downloadPng = () => {
    canvasRef.current
      ?.querySelector('canvas')
      ?.toBlob(blob => blob && downloadBlob(blob, `${fileName}.png`), 'image/png');
  };

  const downloadSvg = () => {
    const svg = svgRef.current?.querySelector('svg');
    if (!svg) return;
    const markup = new XMLSerializer().serializeToString(svg);
    downloadBlob(new Blob([markup], { type: 'image/svg+xml' }), `${fileName}.svg`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share your link in bio</DialogTitle>
          <DialogDescription>
            Put it in your Instagram or TikTok bio, your emails and your website.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <div className="truncate rounded-lg border bg-muted/40 px-3 py-2 text-sm">{url}</div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={onCopy}>
              <Copy />
              Copy link
            </Button>
            <Button variant="outline" asChild>
              <a href={openUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink />
                Open
              </a>
            </Button>
            {canShare && (
              <Button type="button" variant="outline" onClick={onShare}>
                <Share2 />
                Share
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-3 border-t pt-4">
          <div className="space-y-0.5">
            <h3 className="text-sm font-medium">QR code</h3>
            <p className="text-sm text-muted-foreground">
              For posters, flyers and bulletins. It opens{' '}
              <span className="break-all">{qrShownUrl}</span>, even if you change your store
              homepage. If you change your store URL, download a new code.
            </p>
          </div>
          <div
            ref={svgRef}
            className="mx-auto w-fit rounded-lg border bg-white p-2"
            role="img"
            aria-label={`QR code for ${qrShownUrl}`}
          >
            <QRCode value={qrUrl} size={160} renderAs="svg" level="M" includeMargin />
          </div>
          <div ref={canvasRef} hidden>
            <QRCode value={qrUrl} size={canvasSize} renderAs="canvas" level="M" includeMargin />
          </div>
          <div className="flex justify-center gap-2">
            <Button type="button" variant="outline" onClick={downloadPng}>
              <Download />
              Download PNG
            </Button>
            <Button type="button" variant="outline" onClick={downloadSvg}>
              <Download />
              Download SVG
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
